import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    forwardRef,
    input,
    model,
    signal,
    ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { InputSelectComponent } from '../m020-c030-input-select/m020-c030-input-select.component';

interface RangeValue {
    from?: number;
    to?: number;
}

@Component({
    standalone: true,
    imports: [InputSelectComponent],
    selector: './ui-input-range',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    templateUrl: './m020-c100-input-range.component.html',
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => InputRangeComponent), multi: true }],
})
export class InputRangeComponent implements ControlValueAccessor {
    withNullSelect = input(false, { transform: booleanAttribute });
    options = input.required<Array<{ value: number; label: string }>>();
    nullSelectCustomLabel = input('Option wählen');
    optionsSorted = computed(() => {
        return this.options().toSorted((a, b) => a.value - b.value);
    });

    only = input<'min' | 'max'>();

    open = signal(false);

    fromOptions = computed(() => {
        const rangeTo = this.range().to;
        const options = rangeTo ? this.options().filter((opt) => opt.value <= rangeTo) : this.options();
        return options.map((opt) => ({
            ...opt,
            value: opt.value.toString(),
        }));
    });

    toOptions = computed(() => {
        const rangeFrom = this.range().from;

        const options = rangeFrom ? this.options().filter((opt) => opt.value >= rangeFrom) : this.options();
        return options.map((opt) => ({
            ...opt,
            value: opt.value.toString(),
        }));
    });

    range = model<{ from?: number; to?: number }>({});

    disabled = model(false);

    onChange?: (range: RangeValue | undefined) => void;
    onTouch?: () => void;

    writeValue(value: RangeValue) {
        this.range.set(value);
    }

    registerOnChange(fn: () => void) {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void) {
        this.onTouch = fn;
    }

    setDisabledState?(isDisabled: boolean) {
        this.disabled.set(isDisabled);
    }

    setFrom(from: number) {
        if (this.disabled()) {
            return;
        }

        this.range.set({ ...this.range(), from });
        this.onChange?.(this.range());
        this.onTouch?.();
    }
    setTo(to: number) {
        if (this.disabled()) {
            return;
        }

        this.range.set({ ...this.range(), to });
        this.onChange?.(this.range());
        this.onTouch?.();
    }
}
